import { useUserMe } from 'api/goodtrust/user'
import { TwoLineButtonContent } from 'components/button/TwoLineButtonContent'
import { SubmitType } from 'components/directive/directiveFormLayout/DirectiveFormLayout'
import { useModal } from 'components/modal/Modal'
import { UpsellModal } from 'components/modal/upsellModal/UpsellModal'
import { Picture } from 'components/picture/Picture'
import { useDirectiveUpsellPlanType } from 'logic/subscription/upsell/useDirectiveUpsellPlanType'
import { planSpecs } from 'logic/subscription/plan/spec'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'
import { FieldValues, FormState } from 'react-hook-form'
import { EventArgs } from 'utils/analytics/types'

/**
 * Returns a beforeExit hook function that should be called before the user exits.
 * The function checks if the upsell modal should be triggered. That is determined by whether the user has a plan.
 *   The return value of the function is a boolean promise that tells the caller whether they should continue with the exit.
 *   The caller is meant to wait for the promise to resolve.
 *
 */
export const useUpsellModalOnExit = (
  dir_type: EventArgs['directive_paywall_hit']['dir_type'],
  formState?: FormState<FieldValues>,
  onSubmit?: (type: SubmitType, beforeExit?: () => Promise<boolean>) => Promise<void>
) => {
  const { t } = useTranslation('directive')
  const onCloseRef = useRef<(() => void) | undefined>(undefined)
  const upsellType = useDirectiveUpsellPlanType()
  const upsellSpec = upsellType && planSpecs[upsellType]
  const [modal, openModal, _closeModal, isOpen] = useModal(({ isOpen, close }) =>
    isOpen ? (
      <UpsellModal
        title={t('directive.upsell_modal.on_exit_title', {
          planName: upsellType ? t(`directive.upsell_modal.${upsellType}.display_name`) : 'Premium',
        })}
        picture={<Picture imageKey="illustrations/ready" sizes="160px" />}
        primaryButtonText={
          <TwoLineButtonContent
            line1={t('directive.upsell_modal.button_line_1')}
            line2={t('directive.upsell_modal.button_line_2')}
          />
        }
        secondaryButtonText={t('directive.upsell_modal.on_exit_secondary_button')}
        buttonOrientation={'horizontal'}
        paywallHitEventArgs={{ dir_type, place: 'before_leave_upsell' }}
        close={close}
      />
    ) : null
  )

  useEffect(() => {
    if (!isOpen) {
      onCloseRef.current?.()
    }
  }, [isOpen])

  const userMe = useUserMe()
  const shouldTrigger = !!(userMe.data?.ok && upsellSpec)

  const beforeExit = async (): Promise<boolean> => {
    if (!shouldTrigger) return true
    openModal()
    await new Promise<void>((resolve) => {
      onCloseRef.current = resolve
    })

    if (formState?.isDirty) {
      await onSubmit?.('save', async () => false)
    }

    return true
  }

  return { beforeExit, modal }
}
